import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col } from 'react-bootstrap';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { OfferCoachDocumentService } from './service';
import { ErrorToast } from '../../shared/toasters/toasters';
import Pagination from '../../Common/Pagination';

// Documents coaches sent with their applications. Read only - the endpoint
// offers a paginated list and nothing else, so there is no edit or delete here.
const OfferCoachDocumentList = () => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const entriesPerPageOptions = [
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '30', value: 30 },
    { label: '40', value: 40 },
  ];

  const handleEntriesPerPageChange = (selectedOption: any) => {
    setEntriesPerPage(selectedOption ? selectedOption.value : 10);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // A document either answers one of the fixed categories or carries the free
  // text name the company asked for - never both.
  const documentLabel = (item: any) => {
    if (item?.customName) {
      return item.customName;
    }

    return item?.type
      ? t(`offerCoachDocument.documentType.${item.type}`, item.type)
      : 'N/A';
  };

  const formatSize = (bytes: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fetchData = async () => {
    try {
      const response = await OfferCoachDocumentService.getAll({
        page: currentPage,
        perPage: entriesPerPage,
      });
      setList(response.data);
      setTotalPages(response.totalPages);
    } catch (error) {
      ErrorToast(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, entriesPerPage]);

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody className="card-body">
            <div className="d-sm-flex align-items-center mb-3">
              <ul className="list-inline me-auto my-1">
                <li className="list-inline-item">
                  <Select
                    placeholder={t('offerCoachDocument.entriesPerPage')}
                    options={entriesPerPageOptions}
                    onChange={handleEntriesPerPageChange}
                    defaultValue={entriesPerPageOptions.find(
                      (option) => option.value === entriesPerPage
                    )}
                    classNamePrefix="react-select"
                  />
                </li>
              </ul>
            </div>
            <div className="table-responsive">
              <table className="table table-hover" id="pc-dt-simple-1">
                <thead>
                  <tr>
                    <th>{t('offerCoachDocument.id')}</th>
                    <th>{t('offerCoachDocument.document')}</th>
                    <th>{t('offerCoachDocument.file')}</th>
                    <th>{t('offerCoachDocument.coach')}</th>
                    <th>{t('offerCoachDocument.job')}</th>
                    <th>{t('offerCoachDocument.company')}</th>
                    <th>{t('offerCoachDocument.issuedAt')}</th>
                    <th>{t('offerCoachDocument.createdAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item: any, index: number) => (
                    <tr key={index}>
                      <td>{item.id}</td>
                      <td>{documentLabel(item)}</td>
                      <td>
                        <span
                          className="d-inline-block text-truncate"
                          style={{ maxWidth: 220 }}
                          title={item.file?.nameOriginal}
                        >
                          {item.file?.nameOriginal || 'N/A'}
                        </span>
                        {item.file?.size ? (
                          <small className="text-muted d-block">
                            {formatSize(Number(item.file.size))}
                          </small>
                        ) : null}
                      </td>
                      <td>
                        {item.offer?.user?.firstName
                          ? `${item.offer.user.firstName} ${item.offer.user.lastName}`
                          : 'N/A'}
                        {item.offer?.user?.email ? (
                          <small className="text-muted d-block">
                            {item.offer.user.email}
                          </small>
                        ) : null}
                      </td>
                      <td>{item.offer?.job?.title || 'N/A'}</td>
                      <td>{item.offer?.job?.company?.name || 'N/A'}</td>
                      <td>
                        {item.issuedAt
                          ? new Date(item.issuedAt).toLocaleDateString()
                          : '-'}
                      </td>
                      <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              previousLabel={t('offerCoachDocument.previous')}
              nextLabel={t('offerCoachDocument.next')}
            />
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default OfferCoachDocumentList;
